import { edgeFunctions, supabase } from '../lib/supabase'
import type {
  StorageBackupPage,
  StorageBackupProgress,
  StorageBackupRequest,
  StorageBucketFolders,
  StorageFolderRow,
  StorageSignedFile,
} from '../types/storageBackup'

const PAGE_SIZE = 100

type FileSystemWritableFileStreamLike = {
  write: (data: BufferSource | Blob | string) => Promise<void>
  close: () => Promise<void>
}

type FileSystemFileHandleLike = {
  createWritable: () => Promise<FileSystemWritableFileStreamLike>
}

type FileSystemDirectoryHandleLike = {
  kind: 'directory'
  name: string
  getDirectoryHandle: (
    name: string,
    opts?: { create?: boolean },
  ) => Promise<FileSystemDirectoryHandleLike>
  getFileHandle: (
    name: string,
    opts?: { create?: boolean },
  ) => Promise<FileSystemFileHandleLike>
}

export function isShowDirectoryPickerWriteAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as Window & { showDirectoryPicker?: unknown }).showDirectoryPicker ===
      'function'
  )
}

/** Evita "File picker already active" em double-click / chamadas concorrentes. */
let directoryPickerActive = false

export async function pickBackupDirectory(): Promise<FileSystemDirectoryHandleLike | null> {
  if (!isShowDirectoryPickerWriteAvailable()) return null
  if (directoryPickerActive) return null

  const showDirectoryPicker = (
    window as unknown as {
      showDirectoryPicker: (opts?: {
        mode?: 'read' | 'readwrite'
      }) => Promise<FileSystemDirectoryHandleLike>
    }
  ).showDirectoryPicker

  directoryPickerActive = true
  try {
    return await showDirectoryPicker({ mode: 'readwrite' })
  } catch (err) {
    if (err && typeof err === 'object' && 'name' in err && (err as { name: string }).name === 'AbortError') {
      return null
    }
    const message = err instanceof Error ? err.message : String(err)
    if (message.toLowerCase().includes('file picker already active')) {
      return null
    }
    throw err
  } finally {
    directoryPickerActive = false
  }
}

function groupByBucket(rows: StorageFolderRow[]): StorageBucketFolders[] {
  const map = new Map<string, StorageFolderRow[]>()
  for (const row of rows) {
    const list = map.get(row.bucket_id) ?? []
    list.push(row)
    map.set(row.bucket_id, list)
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b, 'pt-BR'))
    .map(([bucketId, folders]) => {
      const sorted = [...folders].sort((a, b) => {
        if (a.folder_name == null) return -1
        if (b.folder_name == null) return 1
        return a.folder_name.localeCompare(b.folder_name, 'pt-BR')
      })
      return {
        bucketId,
        folders: sorted,
        totalFiles: sorted.reduce((s, f) => s + Number(f.file_count || 0), 0),
        totalSizeBytes: sorted.reduce((s, f) => s + Number(f.total_size_bytes || 0), 0),
      }
    })
}

export const storageBackupService = {
  async listFolders(): Promise<StorageBucketFolders[]> {
    const { data, error } = await supabase.rpc('list_storage_folders')
    if (error) throw error

    const rows = ((data ?? []) as Array<{
      bucket_id: string
      folder_name: string | null
      file_count: number | string
      total_size_bytes: number | string
    }>).map(
      (r): StorageFolderRow => ({
        bucket_id: r.bucket_id,
        folder_name: r.folder_name,
        file_count: Number(r.file_count) || 0,
        total_size_bytes: Number(r.total_size_bytes) || 0,
      }),
    )

    return groupByBucket(rows)
  },

  async fetchBackupPage(params: {
    bucket: string
    prefix?: string | null
    rootOnly?: boolean
    offset: number
    limit?: number
  }): Promise<StorageBackupPage> {
    const { data, error } = await edgeFunctions.invoke('storage-folder-backup', {
      bucket: params.bucket,
      prefix: params.prefix ?? '',
      rootOnly: Boolean(params.rootOnly),
      offset: params.offset,
      limit: params.limit ?? PAGE_SIZE,
    })

    const payload = (data ?? {}) as StorageBackupPage & { error?: string; message?: string }

    if (error) {
      if (typeof payload.error === 'string' && payload.error.trim()) {
        throw new Error(payload.error)
      }
      const errMsg = (error as { message?: string }).message || ''
      const status =
        (error as { status?: number; context?: { status?: number } }).status ??
        (error as { context?: { status?: number } }).context?.status
      if (status === 404 || errMsg.toLowerCase().includes('not found')) {
        throw new Error(
          'Função storage-folder-backup não encontrada. Publique a edge function no Supabase.',
        )
      }
      if (status === 401 || errMsg.toLowerCase().includes('jwt')) {
        throw new Error('Sessão expirada. Faça login novamente.')
      }
      throw new Error(errMsg || 'Erro ao solicitar URLs de backup')
    }

    if (payload.error) {
      throw new Error(payload.error)
    }

    return {
      bucket: payload.bucket,
      prefix: payload.prefix ?? '',
      rootOnly: Boolean(payload.rootOnly),
      offset: Number(payload.offset) || 0,
      limit: Number(payload.limit) || PAGE_SIZE,
      hasMore: Boolean(payload.hasMore),
      files: Array.isArray(payload.files) ? payload.files : [],
      count: Number(payload.count) || 0,
    }
  },
}

async function ensureNestedDirectory(
  root: FileSystemDirectoryHandleLike,
  parts: string[],
): Promise<FileSystemDirectoryHandleLike> {
  let current = root
  for (const part of parts) {
    if (!part || part === '.' || part === '..') continue
    current = await current.getDirectoryHandle(part, { create: true })
  }
  return current
}

async function writeFileFromUrl(
  root: FileSystemDirectoryHandleLike,
  relativePath: string,
  signedUrl: string,
): Promise<void> {
  const segments = relativePath.split('/').filter(Boolean)
  if (segments.length === 0) {
    throw new Error('Caminho de arquivo inválido')
  }
  const fileName = segments[segments.length - 1]
  const dirParts = segments.slice(0, -1)
  const dir = await ensureNestedDirectory(root, dirParts)
  const fileHandle = await dir.getFileHandle(fileName, { create: true })
  const writable = await fileHandle.createWritable()

  const res = await fetch(signedUrl)
  if (!res.ok) {
    await writable.close().catch(() => undefined)
    throw new Error(`HTTP ${res.status}`)
  }
  const blob = await res.blob()
  await writable.write(blob)
  await writable.close()
}

export type RunStorageBackupOptions = {
  request: StorageBackupRequest
  rootDir: FileSystemDirectoryHandleLike
  signal?: AbortSignal
  onProgress?: (progress: StorageBackupProgress) => void
}

/**
 * Baixa páginas de URLs assinadas e grava em disco sob {bucket}/...
 */
export async function runStorageBackup(options: RunStorageBackupOptions): Promise<StorageBackupProgress> {
  const { request, rootDir, signal, onProgress } = options
  const failed: StorageBackupProgress['failed'] = []
  let done = 0
  let total = Math.max(0, request.estimatedTotal ?? 0)
  let offset = 0
  let hasMore = true

  const emit = (currentPath: string | null) => {
    onProgress?.({ done, total, currentPath, failed: [...failed] })
  }

  emit(null)

  while (hasMore) {
    if (signal?.aborted) {
      throw new DOMException('Backup cancelado', 'AbortError')
    }

    const page = await storageBackupService.fetchBackupPage({
      bucket: request.bucket,
      prefix: request.prefix,
      rootOnly: request.rootOnly,
      offset,
      limit: PAGE_SIZE,
    })

    const files: StorageSignedFile[] = page.files
    if (files.length === 0) {
      hasMore = false
      break
    }

    // Se a estimativa veio baixa (ex.: placeholders), ajusta pelo progresso real
    if (!page.hasMore) {
      total = Math.max(total, done + files.length)
    } else if (total < done + files.length) {
      total = done + files.length + PAGE_SIZE
    }

    for (const file of files) {
      if (signal?.aborted) {
        throw new DOMException('Backup cancelado', 'AbortError')
      }

      const relativePath = `${request.bucket}/${file.path}`
      emit(relativePath)

      try {
        await writeFileFromUrl(rootDir, relativePath, file.signedUrl)
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        failed.push({ path: relativePath, error: message })
      }

      done += 1
      emit(relativePath)
    }

    hasMore = page.hasMore
    offset += page.limit
  }

  total = Math.max(total, done)
  const finalProgress: StorageBackupProgress = {
    done,
    total,
    currentPath: null,
    failed,
  }
  onProgress?.(finalProgress)
  return finalProgress
}

export type RunStorageBackupAllOptions = {
  requests: StorageBackupRequest[]
  rootDir: FileSystemDirectoryHandleLike
  signal?: AbortSignal
  onProgress?: (progress: StorageBackupProgress) => void
}

/**
 * Backup sequencial de vários buckets/pastas na mesma pasta local.
 * Progresso agrega done/total de todos os requests.
 */
export async function runStorageBackupAll(
  options: RunStorageBackupAllOptions,
): Promise<StorageBackupProgress> {
  const { requests, rootDir, signal, onProgress } = options
  const failed: StorageBackupProgress['failed'] = []
  let completedBefore = 0
  let total = requests.reduce((s, r) => s + Math.max(0, r.estimatedTotal ?? 0), 0)

  onProgress?.({ done: 0, total, currentPath: null, failed: [] })

  for (let i = 0; i < requests.length; i++) {
    if (signal?.aborted) {
      throw new DOMException('Backup cancelado', 'AbortError')
    }

    const request = requests[i]!
    const estimatedAfter = requests
      .slice(i + 1)
      .reduce((s, r) => s + Math.max(0, r.estimatedTotal ?? 0), 0)

    const result = await runStorageBackup({
      request,
      rootDir,
      signal,
      onProgress: (p) => {
        const done = completedBefore + p.done
        total = Math.max(total, done + estimatedAfter, completedBefore + p.total + estimatedAfter)
        onProgress?.({
          done,
          total,
          currentPath: p.currentPath,
          failed: [...failed, ...p.failed],
        })
      },
    })

    failed.push(...result.failed)
    completedBefore += result.done
    total = Math.max(total, completedBefore + estimatedAfter)
  }

  const finalProgress: StorageBackupProgress = {
    done: completedBefore,
    total: Math.max(total, completedBefore),
    currentPath: null,
    failed,
  }
  onProgress?.(finalProgress)
  return finalProgress
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = bytes
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i += 1
  }
  return `${value.toFixed(i === 0 ? 0 : 2)} ${units[i]}`
}

export function folderDisplayName(folderName: string | null): string {
  return folderName == null ? '(raiz)' : folderName
}
